/**
 * PC Base Component
 *
 * SVG component for player home base area using the PC skin.
 * Features a Windows-style desktop UI with a monitor frame, title bar,
 * screen area with sky-blue gradient, 4 pawn spot ellipses, and a taskbar.
 *
 * This component is color-agnostic and accepts a colorScheme prop to render
 * bases for different players (Red, Blue, Yellow, Green).
 */

import React, { type ReactElement } from 'react';
import type { BaseProps, PawnProps } from '../../constants/types';
import { renderPawnAtPosition } from '../basics/utils/utils';
import type { PcBaseColorScheme } from './constants/baseColorSchemes';

// Helper constants for rank display
const RANK_ICONS = ['🏆', '🥈', '🥉', '🏅'] as const;
const RANK_ORDINALS = ['1st', '2nd', '3rd', '4th'] as const;

function getRankIcon(rank: number): string {
  return RANK_ICONS[Math.min(rank - 1, 3)];
}

function getRankOrdinal(rank: number): string {
  if (rank >= 1 && rank <= 4) {
    return RANK_ORDINALS[rank - 1];
  }
  return `${rank.toString()}th`;
}

export interface PcBaseComponentProps extends BaseProps {
  colorScheme: PcBaseColorScheme;
  PawnComponent: React.ComponentType<PawnProps>;
  onPawn1Click?: () => void;
  onPawn2Click?: () => void;
  onPawn3Click?: () => void;
  onPawn4Click?: () => void;
  shouldAnimatePawns?: boolean;
  /** Player's finishing rank (1-4) - when set, shows rank badge instead of pawns */
  playerRank?: number;
  /** Whether the player has voluntarily left the game */
  hasLeft?: boolean;
}

const PcBaseComponent = React.memo(function PcBaseComponent({
  viewBox = '0 0 486 523',
  className = '',
  isActive = false,
  isHovered = false,
  isSelected = false,
  showEntrance = false,
  pawn1 = false,
  pawn2 = false,
  pawn3 = false,
  pawn4 = false,
  pawn1Active = false,
  pawn1Hovered = false,
  pawn1Selected = false,
  pawn1Moveable = false,
  pawn2Active = false,
  pawn2Hovered = false,
  pawn2Selected = false,
  pawn2Moveable = false,
  pawn3Active = false,
  pawn3Hovered = false,
  pawn3Selected = false,
  pawn3Moveable = false,
  pawn4Active = false,
  pawn4Hovered = false,
  pawn4Selected = false,
  pawn4Moveable = false,
  colorScheme,
  PawnComponent,
  onPawn1Click,
  onPawn2Click,
  onPawn3Click,
  onPawn4Click,
  shouldAnimatePawns = false,
  playerRank,
  hasLeft = false
}: PcBaseComponentProps): ReactElement {
  const {
    name,
    ariaLabel,
    ellipseShadow,
    gradientStart,
    gradientStop1,
    gradientStop2,
    gradientStop3,
    gradientStop4,
    gradientEnd
  } = colorScheme;

  const svgClassName = [
    className,
    showEntrance ? 'base-entrance' : '',
    isActive ? 'base-active' : '',
    isHovered ? 'base-hovered' : '',
    isSelected ? 'base-selected' : ''
  ]
    .filter(Boolean)
    .join(' ');

  // Prefix all gradient IDs to avoid conflicts when 4 bases render simultaneously
  const prefix = `${name}PcBase_`;

  return (
    <svg
      viewBox={viewBox}
      className={svgClassName}
      xmlns="http://www.w3.org/2000/svg"
      aria-label={ariaLabel}
    >
      <defs>
        {/* Screen background: sky-blue gradient */}
        <linearGradient
          id={`${prefix}screenBg`}
          x1="77.66"
          y1="259.26"
          x2="418.4"
          y2="259.26"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#72bbfc" />
          <stop offset=".48" stopColor="#a7dffc" />
          <stop offset="1" stopColor="#e6fbff" />
        </linearGradient>

        {/* Grass/ground gradient at bottom of screen */}
        <linearGradient
          id={`${prefix}grass`}
          x1="-3143.95"
          y1="358.9"
          x2="-2713.43"
          y2="358.9"
          gradientTransform="translate(-2529.99) rotate(-180) scale(1 -1)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#006837" />
          <stop offset=".32" stopColor="#007039" />
          <stop offset=".82" stopColor="#008841" />
          <stop offset="1" stopColor="#009245" />
        </linearGradient>

        {/* Player-specific gradient for pawn ellipses (top-left) */}
        <linearGradient
          id={`${prefix}ellipse1`}
          x1="141.27"
          y1="200.57"
          x2="215.29"
          y2="200.57"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={gradientStart} />
          <stop offset=".27" stopColor={gradientStop1} />
          <stop offset=".49" stopColor={gradientStop2} />
          <stop offset=".68" stopColor={gradientStop3} />
          <stop offset=".86" stopColor={gradientStop4} />
          <stop offset="1" stopColor={gradientEnd} />
        </linearGradient>

        {/* Player-specific gradient for pawn ellipses (top-right) */}
        <linearGradient
          id={`${prefix}ellipse2`}
          x1="280.81"
          y1="199.23"
          x2="354.82"
          y2="199.23"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={gradientStart} />
          <stop offset=".27" stopColor={gradientStop1} />
          <stop offset=".49" stopColor={gradientStop2} />
          <stop offset=".68" stopColor={gradientStop3} />
          <stop offset=".86" stopColor={gradientStop4} />
          <stop offset="1" stopColor={gradientEnd} />
        </linearGradient>

        {/* Player-specific gradient for pawn ellipses (bottom-left) */}
        <linearGradient
          id={`${prefix}ellipse3`}
          x1="141.26"
          y1="321.26"
          x2="215.27"
          y2="321.26"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={gradientStart} />
          <stop offset=".27" stopColor={gradientStop1} />
          <stop offset=".49" stopColor={gradientStop2} />
          <stop offset=".68" stopColor={gradientStop3} />
          <stop offset=".86" stopColor={gradientStop4} />
          <stop offset="1" stopColor={gradientEnd} />
        </linearGradient>

        {/* Player-specific gradient for pawn ellipses (bottom-right) */}
        <linearGradient
          id={`${prefix}ellipse4`}
          x1="280.81"
          y1="321.94"
          x2="354.82"
          y2="321.94"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor={gradientStart} />
          <stop offset=".27" stopColor={gradientStop1} />
          <stop offset=".49" stopColor={gradientStop2} />
          <stop offset=".68" stopColor={gradientStop3} />
          <stop offset=".86" stopColor={gradientStop4} />
          <stop offset="1" stopColor={gradientEnd} />
        </linearGradient>

        {/* Radial gradient: window close button (red) */}
        <radialGradient
          id={`${prefix}btnClose`}
          cx="355.79"
          cy="130.58"
          fx="355.79"
          fy="130.58"
          r="16.87"
          gradientTransform="translate(122.19 -151.71) rotate(36.56) scale(1 .67)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#fff" />
          <stop offset=".42" stopColor="#ff4a2c" />
        </radialGradient>

        {/* Radial gradient: window minimize button (blue) */}
        <radialGradient
          id={`${prefix}btnMin`}
          cx="337.42"
          cy="130.58"
          fx="337.42"
          fy="130.58"
          r="16.87"
          gradientTransform="translate(118.58 -140.77) rotate(36.56) scale(1 .67)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#29abe2" />
          <stop offset=".42" stopColor="#0071bc" />
        </radialGradient>

        {/* Radial gradient: window maximize button (blue) */}
        <radialGradient
          id={`${prefix}btnMax`}
          cx="318.75"
          fx="318.75"
          r="16.87"
          gradientTransform="translate(114.9 -129.65) rotate(36.56) scale(1 .67)"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0" stopColor="#29abe2" />
          <stop offset=".42" stopColor="#0071bc" />
        </radialGradient>
      </defs>

      {/*
        Translate PC content up by ~29px to align with basics/celeste frame position.
        PC frame starts at y≈71, basics at y≈42. All content (including pawns) shifts together.
      */}
      <g transform="translate(0, -29)">
        {/* Monitor base/stand shadow (dark) */}
        <path
          fill="#383838"
          d="M51.89,423.67v33.65c0,9.85,5.27,19.04,13.95,23.7.07.04.14.07.21.11,10.06,5.31,19.55,4.09,31.02,3.56,13.98-.65,27.96,1.41,41.95,1.4,27.88,0,55.77.04,83.65,0,20.04-.03,39.79-.01,53.91,0,21.62.02,32.44.03,33.56.05,47.51.74,51.29,3.95,68.14,0,11.65-2.73,15.82-5.67,26.7-5.52,10.31.14,16.91,2.92,25.43-.51,6.96-2.81,11.95-9.41,11.95-9.41.01-.02.03-.03.04-.05,2.02-2.35,3.08-5.37,3.05-8.47-.14-14.62-.28-29.24-.42-43.86-62.83,9.43-135.79,16.19-216.98,16.15-64.56-.04-123.63-4.37-176.17-10.81Z"
        />

        {/* Monitor base/stand (grey) */}
        <path
          fill="#848484"
          d="M51.89,399.94v44.41c2.7,4,7.17,9.36,14.15,13.05,10.06,5.31,19.55,4.09,31.02,3.56,13.98-.65,27.96,1.41,41.95,1.4,27.88,0,55.77.04,83.65,0,20.04-.03,39.79-.01,53.91,0,21.62.02,32.44.03,33.56.05,47.51.74,51.29,3.95,68.14,0,11.65-2.73,15.82-5.67,26.7-5.52,10.31.14,16.91,2.92,25.43-.51,6.96-2.81,11.95-9.41,11.95-9.41,1.4-1.62,2.44-3.09,3.14-4.15-.16-16.08-.31-32.15-.47-48.23-62.83,9.43-135.79,16.19-216.98,16.15-64.56-.04-123.63-4.37-176.17-10.81Z"
        />

        {/* Monitor outer frame (#ddd) */}
        <path
          fill="#ddd"
          d="M444.84,107.98v302.54c0,20.37-16.52,36.89-36.89,36.89H88.12c-6.06,0-11.77-1.46-16.82-4.05-11.92-6.11-20.07-18.53-20.07-32.84V107.98c0-18.69,13.9-34.14,31.94-36.56,1.62-.22,3.27-.33,4.95-.33h319.83c1.6,0,3.16.1,4.71.3h.01c5.98.76,11.52,2.96,16.25,6.24,4.78,3.31,8.74,7.71,11.51,12.85,2.81,5.21,4.41,11.17,4.41,17.5Z"
        />

        {/* Screen background (sky-blue gradient) */}
        <rect
          fill={`url(#${prefix}screenBg)`}
          x="77.66"
          y="103.18"
          width="340.74"
          height="312.15"
        />

        {/* Ground/grass area at bottom of screen */}
        <path
          fill={`url(#${prefix}grass)`}
          d="M77.66,335.99v79.34s340.74,0,340.74,0v-64.88c-12.33-3.49-27.35-1.76-54.08-7.22-42.6-8.71-43.47-21.08-80.59-32.11-61.22-18.2-120.94-3.02-146.96,3.59-20.63,5.24-44.17,11.23-59.11,21.28Z"
        />

        {/* Bottom-left quadrant overlay (blue tint) */}
        <path
          fill="#6478d7"
          opacity=".51"
          d="M248.04,253.69v133.4h-102.65c-6.26,0-12.14-1.56-17.31-4.31-6.41-3.41-11.69-8.66-15.15-15.04-2.82-5.22-4.43-11.19-4.43-17.54v-96.51h139.55Z"
        />

        {/* Top-right quadrant overlay (blue tint) */}
        <path
          fill="#6478d7"
          opacity=".51"
          transform="translate(635.62 373.97) rotate(180)"
          d="M248.04,120.28h139.53v133.4h-102.64c-20.36,0-36.89-16.53-36.89-36.89v-96.52h0Z"
        />

        {/* Top-left quadrant (lighter overlay) */}
        <path
          fill="#f7f7f7"
          opacity=".82"
          d="M248.04,120.28v133.4H108.5v-96.51c0-4.23.71-8.3,2.03-12.08,5-14.45,18.72-24.81,34.86-24.81h102.65Z"
        />

        {/* Title bar background (blue) */}
        <path
          fill="#2468ff"
          d="M386.03,146.65H110.02c4.52-15.25,18.64-26.37,35.36-26.37h205.29c16.72,0,30.84,11.12,35.36,26.37Z"
        />

        {/* Bottom-right quadrant (lighter overlay) */}
        <path
          fill="#f7f7f7"
          opacity=".82"
          transform="translate(-2.59 638.2) rotate(-90)"
          d="M251.1,250.63h133.4v139.53h-96.52c-20.36,0-36.89-16.53-36.89-36.89v-102.64h0Z"
        />

        {/* Pawn spot ellipses (original PC positions inside translate group) */}
        {playerRank === undefined && !hasLeft && (
          <g>
            <ellipse fill={ellipseShadow} cx="178.28" cy="197.88" rx="37.01" ry="34.51" />
            <ellipse
              fill={`url(#${prefix}ellipse1)`}
              cx="178.28"
              cy="200.57"
              rx="37.01"
              ry="34.51"
            />
            <ellipse fill={ellipseShadow} cx="317.81" cy="196.53" rx="37.01" ry="34.51" />
            <ellipse
              fill={`url(#${prefix}ellipse2)`}
              cx="317.81"
              cy="199.23"
              rx="37.01"
              ry="34.51"
            />
            <ellipse fill={ellipseShadow} cx="178.27" cy="318.57" rx="37.01" ry="34.51" />
            <ellipse
              fill={`url(#${prefix}ellipse3)`}
              cx="178.27"
              cy="321.26"
              rx="37.01"
              ry="34.51"
            />
            <ellipse fill={ellipseShadow} cx="317.81" cy="319.24" rx="37.01" ry="34.51" />
            <ellipse
              fill={`url(#${prefix}ellipse4)`}
              cx="317.81"
              cy="321.94"
              rx="37.01"
              ry="34.51"
            />
          </g>
        )}

        {/* Taskbar background (blue) */}
        <rect fill="#2468ff" x="77.66" y="399.66" width="340.74" height="15.67" />

        {/* System tray area (light blue) */}
        <rect fill="#278eff" x="382.56" y="399.66" width="35.85" height="15.67" />

        {/* Start button (green) */}
        <path
          fill="#00bf36"
          d="M77.66,399.66h45.46c3.87,0,7.01,3.14,7.01,7.01h0c0,4.78-3.88,8.67-8.67,8.67h-43.8v-15.67h0Z"
        />

        {/* Window border (blue outline) */}
        <path
          fill="#2468ff"
          d="M353.56,128.28c15.15,0,27.47,12.32,27.47,27.47v195.87c0,15.15-12.32,27.47-27.47,27.47h-209.77c-15.15,0-27.47-12.32-27.47-27.47v-195.87c0-15.15,12.32-27.47,27.47-27.47h209.77M353.56,120.28h-209.77c-19.59,0-35.47,15.88-35.47,35.47v195.87c0,19.59,15.88,35.47,35.47,35.47h209.77c19.59,0,35.47-15.88,35.47-35.47v-195.87c0-19.59-15.88-35.47-35.47-35.47h0Z"
        />

        {/* Window control buttons group */}
        <g>
          {/* Close button (red radial) */}
          <rect
            fill={`url(#${prefix}btnClose)`}
            x="354.85"
            y="128.58"
            width="13.22"
            height="13.22"
            rx=".82"
            ry=".82"
            stroke="#f2f2f2"
            strokeWidth=".5"
            strokeMiterlimit="10"
          />
          {/* Minimize button (blue radial) */}
          <rect
            fill={`url(#${prefix}btnMin)`}
            x="336.48"
            y="128.58"
            width="13.22"
            height="13.22"
            rx=".82"
            ry=".82"
            stroke="#f2f2f2"
            strokeWidth=".5"
            strokeMiterlimit="10"
          />
          {/* Maximize button (blue radial) */}
          <rect
            fill={`url(#${prefix}btnMax)`}
            x="317.81"
            y="128.58"
            width="13.22"
            height="13.22"
            rx=".82"
            ry=".82"
            stroke="#f2f2f2"
            strokeWidth=".5"
            strokeMiterlimit="10"
          />
          {/* Minimize button inner border */}
          <rect
            fill="none"
            x="338.65"
            y="130.75"
            width="8.89"
            height="8.89"
            strokeMiterlimit="10"
          />
          {/* Minimize bar icon */}
          <rect fill="#f2f2f2" x="338.65" y="130.75" width="8.89" height="2.35" />
          {/* Close X icon line 1 */}
          <rect
            fill="#f2f2f2"
            x="355.75"
            y="134.4"
            width="11.44"
            height="1.6"
            transform="translate(712.66 -24.8) rotate(135)"
          />
          {/* Close X icon line 2 */}
          <rect
            fill="#f2f2f2"
            x="355.75"
            y="134.4"
            width="11.44"
            height="1.6"
            transform="translate(521.46 486.39) rotate(-135)"
          />
          {/* Maximize icon */}
          <rect fill="#f2f2f2" x="318.93" y="138.81" width="4.5" height="1.67" />
        </g>

        {/* Title bar text: "Ludoka.exe" */}
        <text
          fontSize="12"
          fontFamily="Minecraft, Minecraft"
          fill="#f2f2f2"
          transform="translate(140.55 139.8)"
        >
          <tspan x="0" y="0">
            Ludoka.exe
          </tspan>
        </text>

        {/* Taskbar Start label */}
        <text
          fontSize="12"
          fontFamily="Minecraft, Minecraft"
          fill="#f2f2f2"
          transform="translate(83.52 412)"
        >
          <tspan x="0" y="0">
            Start
          </tspan>
        </text>

        {/* System clock */}
        <text
          fontSize="6.97"
          fontFamily="Minecraft, Minecraft"
          fill="#f2f2f2"
          transform="translate(398.65 410.51)"
        >
          <tspan x="0" y="0">
            17:00
          </tspan>
        </text>

        {/* Start button icon (Windows flag-style) */}
        <g>
          <rect
            fill="#fffb64"
            stroke="#aa9d00"
            strokeWidth=".5"
            strokeMiterlimit="10"
            x="125.32"
            y="131.29"
            width="10.07"
            height="8.74"
            rx="1.21"
            ry="1.21"
          />
          <path
            fill="#fffb64"
            stroke="#aa9d00"
            strokeWidth=".5"
            strokeMiterlimit="10"
            d="M134.78,140.03h-8.5c-.49,0-.86-.45-.75-.93l1-4.64c.08-.35.39-.61.75-.61h8.5c.49,0,.86.45.75.93l-1,4.64c-.08.35-.39.61-.75.61Z"
          />
        </g>

        {/* End of translate(-29) group — everything below is at standard positions */}
      </g>

      {/* Left badge display when player has voluntarily left the game */}
      {hasLeft && (
        <g>
          {/* Circle background - grey tint (centered in viewBox) */}
          <circle cx="243" cy="230" r="100" fill="#666" />
          <circle cx="243" cy="230" r="85" fill="#444" />

          {/* Exit/Leave door icon */}
          <g transform="translate(183, 170)">
            {/* Door frame */}
            <rect
              x="15"
              y="10"
              width="50"
              height="100"
              rx="4"
              fill="#888"
              stroke="#aaa"
              strokeWidth="3"
            />
            {/* Door */}
            <rect x="20" y="15" width="40" height="90" rx="2" fill="#666" />
            {/* Door handle */}
            <circle cx="52" cy="60" r="4" fill="#ccc" />
            {/* Arrow pointing out */}
            <path
              d="M75 60 L100 60 M90 50 L100 60 L90 70"
              stroke="#fff"
              strokeWidth="6"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        </g>
      )}

      {/* Rank badge display when player has finished (but not left) */}
      {playerRank !== undefined && !hasLeft && (
        <g>
          {/* Trophy circle background (centered for PC window) */}
          <circle cx="243" cy="230" r="100" fill="#FFFECE" />
          <circle cx="243" cy="230" r="90" fill={gradientStop2} />

          {/* Trophy icon and rank text */}
          <text
            x="243"
            y="211"
            textAnchor="middle"
            fontSize="72"
            aria-label={`Rank ${playerRank.toString()} trophy`}
          >
            {getRankIcon(playerRank)}
          </text>
          <text x="243" y="281" textAnchor="middle" fontSize="48" fontWeight="bold" fill="#fff">
            {getRankOrdinal(playerRank)}
          </text>
        </g>
      )}

      {/* Render pawns conditionally (hidden when player has finished or left) */}
      {playerRank === undefined && !hasLeft && (
        <>
          {renderPawnAtPosition(
            'pawn1',
            pawn1,
            PawnComponent,
            {
              isActive: pawn1Active,
              isHovered: pawn1Hovered,
              isSelected: pawn1Selected,
              isMoveable: pawn1Moveable
            },
            onPawn1Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn2',
            pawn2,
            PawnComponent,
            {
              isActive: pawn2Active,
              isHovered: pawn2Hovered,
              isSelected: pawn2Selected,
              isMoveable: pawn2Moveable
            },
            onPawn2Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn3',
            pawn3,
            PawnComponent,
            {
              isActive: pawn3Active,
              isHovered: pawn3Hovered,
              isSelected: pawn3Selected,
              isMoveable: pawn3Moveable
            },
            onPawn3Click,
            shouldAnimatePawns
          )}
          {renderPawnAtPosition(
            'pawn4',
            pawn4,
            PawnComponent,
            {
              isActive: pawn4Active,
              isHovered: pawn4Hovered,
              isSelected: pawn4Selected,
              isMoveable: pawn4Moveable
            },
            onPawn4Click,
            shouldAnimatePawns
          )}
        </>
      )}
    </svg>
  );
});

export default PcBaseComponent;
